import { ContentState } from "draft-js";
import { Map, Set } from "immutable";
import { Api } from "@core/api/api";
import { organizationHeaders } from "@core/api/organizationHeaders";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { PostDto, postFromDto } from "@domain/post/dtos";
import { Post } from "@domain/post/post";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { CourseStatus } from "@domain/lms/course/courseDraft";
import { exerciseFromDto } from "@domain/lms/course/exercise/dtos";
import {
	quizDraftToDto,
	QuizDto,
	quizFromDto,
	QuizParticipantAnswerDto,
	QuizParticipantAnswerFromDto,
	QuizParticipantDto,
	QuizParticipantFromDto,
	QuizResultDto,
} from "./dto";
import { Quiz, QuizParticipant, QuizResult } from "./quiz";
import { QuizDraft } from "./quizDraft";

const QUIZ_URL = "/quizzes";

export class QuizApi {
	constructor(private api: Api) {}

	async get(id: string): Promise<Quiz> {
		const result = await this.api.get<QuizDto>(`${QUIZ_URL}/${id}`);
		return quizFromDto(result.data);
	}

	async getMyQuizzes(status: CourseStatus | "ALL", pageOptions: PaginationOptions): Promise<Page<Quiz>> {
		const result = await this.api.get<Page<QuizDto>>(
			`${QUIZ_URL}?filter=mine&status=${status === "ALL" ? "" : status}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => quizFromDto(c)) };
	}

	async getProfileQuizzes(profileId: string, pageOptions: PaginationOptions): Promise<Page<Quiz>> {
		const result = await this.api.get<Page<QuizDto>>(`${QUIZ_URL}?userId=${profileId}&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((c) => quizFromDto(c)) };
	}

	async getQuizzesParticipants(quizId: string, pageOptions: PaginationOptions): Promise<Page<QuizParticipant>> {
		const result = await this.api.get<Page<QuizParticipantDto>>(
			`${QUIZ_URL}/${quizId}/participants?${pageParams(pageOptions)}`
		);
		return {
			...result.data,
			content: result.data.content.map((quizParticipant) => QuizParticipantFromDto(quizParticipant)),
		};
	}

	async getParticipantAnswer(quizId: string, participantId: string): Promise<Quiz> {
		const result = await this.api.get<QuizParticipantAnswerDto>(`${QUIZ_URL}/${quizId}/participant/${participantId}`);
		return QuizParticipantAnswerFromDto(result.data);
	}

	async getOrganizationQuizzes(organizationId: string, pageOptions: PaginationOptions): Promise<Page<Quiz>> {
		const result = await this.api.get<Page<QuizDto>>(
			`${QUIZ_URL}?organizationId=${organizationId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => quizFromDto(c)) };
	}

	// get quiz by workSpaceId
	// async getWorkspaceQuizzes(workspaceId: string, pageOptions: PaginationOptions): Promise<Page<Quiz>> {
	// 	const result = await this.api.get<Page<QuizDto>>(
	// 		`${QUIZ_URL}?workspaceId=${workspaceId}&${pageParams(pageOptions)}`
	// 	);
	// 	return { ...result.data, content: result.data.content.map((c) => quizFromDto(c)) };
	// }

	// get quiz by Workspace slug
	async getWorkspaceQuizzes(workspaceId: string, pageOptions: PaginationOptions): Promise<Page<Quiz>> {
		const result = await this.api.get<Page<QuizDto>>(
			`${QUIZ_URL}?workspaceId=${workspaceId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => quizFromDto(c)) };
	}

	async createQuiz(draft: QuizDraft, organizationId?: string): Promise<Quiz> {
		const result = await this.api.post<QuizDto>(QUIZ_URL, quizDraftToDto(draft), organizationHeaders(organizationId));
		return quizFromDto(result.data);
	}

	async saveQuiz(draft: QuizDraft, id: string, organizationId?: string): Promise<Quiz> {
		const result = await this.api.put<QuizDto>(
			`${QUIZ_URL}/${id}`,
			quizDraftToDto(draft),
			organizationHeaders(organizationId)
		);
		return quizFromDto(result.data);
	}

	deleteQuiz(quizId: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${QUIZ_URL}/${quizId}`, organizationHeaders(organizationId));
	}

	async validate(quizId: string, answers: Map<string, Set<number>>): Promise<QuizResult> {
		const input = [...answers.entries()].map(([exerciseId, answerIndices]) => ({
			exerciseId,
			answerIndices: answerIndices.toArray(),
		}));

		const result = await this.api.post<QuizResultDto>(`${QUIZ_URL}/${quizId}/validate`, input);
		return { ...result.data, exercises: result.data.exercises.map(exerciseFromDto) };
	}

	async share(quizId: string, message?: ContentState, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${QUIZ_URL}/${quizId}/share`, {
			text: message ? contentStateToString(message) : undefined,
			groupId,
		});
		return postFromDto(result.data);
	}

	async shareResult(quizId: string, message?: ContentState, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${QUIZ_URL}/${quizId}/share-results`, {
			text: message ? contentStateToString(message) : undefined,
			groupId,
		});
		return postFromDto(result.data);
	}

	async search(
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		levels?: ContentLevel[],
		languages?: ContentLanguage[],
		durations?: CourseDuration[],
		providers?: CourseProvider[]
	): Promise<Page<Quiz>> {
		const result = await this.api.get<Page<QuizDto>>(
			`${QUIZ_URL}?query=${query}&${pageParams(pageOptions)}&${filterParams(
				categories,
				levels,
				languages,
				durations,
				providers
			)}`
		);

		return { ...result.data, content: result.data.content.map((q) => quizFromDto(q)) };
	}

	async getSuggestions(pageOptions: PaginationOptions, categories: Category[]): Promise<Page<Quiz>> {
		const result = await this.api.get<Page<QuizDto>>(
			`${QUIZ_URL}/suggested?${pageParams(pageOptions)}&categories=${categories.join(",")}`
		);
		return { ...result.data, content: result.data.content.map((q) => quizFromDto(q)) };
	}
}
